import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";
import { Submission } from "../types/submission";

interface SubmissionContextType {
  submission: Submission | null;
  setSubmission: Dispatch<SetStateAction<Submission | null>>;
  setSubmissionMetadata: (
    metadata: Partial<Submission["submission_metadata"]>,
  ) => void;
  setSubmissionProperties: (submission: Partial<Submission>) => void;
  initializeSubmission: (
    initialData: Partial<Submission>,
    callback?: () => void,
  ) => void;
}

const SubmissionContext = createContext<SubmissionContextType | undefined>(
  undefined,
);

interface SubmissionProviderProps {
  children: ReactNode;
}

export const SubmissionProvider: React.FC<SubmissionProviderProps> = ({
  children,
}) => {
  const [submission, setSubmission] = useState<Submission | null>(null);

  const initializeSubmission = useCallback(
    (initialData: Partial<Submission>, callback: (() => void) | undefined) => {
      setSubmission(
        () =>
          ({
            id: crypto.randomUUID(),
            category_id: "",
            subcategory_id: null,
            sub_subcategory_id: null,
            device_info: {},
            location_info: null,
            confirm_is_original_sound: false,
            grant_perle_and_samsung_full_commercial_right: false,
            submission_metadata: {},
            ...initialData,
          }) as Submission,
      );
      if (callback) {
        callback();
      }
    },
    [],
  );

  const setSubmissionMetadata = useCallback(
    (metadataUpdate: Partial<Submission["submission_metadata"]>) => {
      setSubmission((prevSubmission) => {
        if (!prevSubmission) {
          console.warn("Submission is not initialized. Cannot set metadata.");
          return null;
        }
        return {
          ...prevSubmission,
          submission_metadata: {
            ...prevSubmission.submission_metadata,
            ...metadataUpdate,
          },
        };
      });
    },
    [],
  );

  const setSubmissionProperties = useCallback(
    (submissionUpdate: Partial<Submission>) => {
      setSubmission((prevSubmission) => {
        if (!prevSubmission) {
          console.warn(
            "Submission is not initialized. Cannot update properties.",
          );
          return null;
        }
        return {
          ...prevSubmission,
          ...submissionUpdate,
        };
      });
    },
    [],
  );

  const value = {
    submission,
    setSubmission,
    setSubmissionMetadata,
    initializeSubmission,
    setSubmissionProperties,
  };

  return (
    <SubmissionContext.Provider value={value}>
      {children}
    </SubmissionContext.Provider>
  );
};

export const useSubmission = (): SubmissionContextType => {
  const context = useContext(SubmissionContext);
  if (context === undefined) {
    throw new Error("useSubmission must be used within a SubmissionProvider");
  }
  return context;
};
